/**
 * PortalPublish — « Envoyer au portail client » : le gestionnaire choisit les documents d'une période approuvée
 * à rendre téléchargeables par le client (Batch #14.251).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Globe, Send, Undo2, CheckCircle2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH, Btn, Check, Section, inputCls, err } from "./_shared";

const DEFAULT_DOCS = { paystubs_pdf: true, cheques_pdf: true, cpa005: true, register_pdf: false, register_xlsx: false, register_csv: false };

export default function PortalPublish({ masterId, run, settings, onChanged }) {
  const [kinds, setKinds] = useState([]);
  const [docs, setDocs] = useState(run.portal_docs || DEFAULT_DOCS);
  const [message, setMessage] = useState(run.portal_message || "");
  const [busy, setBusy] = useState("");
  const base = `/admin/payroll/${masterId}/runs/${run.id}`;

  useEffect(() => {
    axios.get(`${API}/admin/payroll/portal-doc-kinds`, { headers: authH() }).then((r) => setKinds(r.data.items || [])).catch(() => setKinds([]));
  }, []);

  const publish = async () => {
    if (!Object.values(docs).some(Boolean)) { toast.error("Choisissez au moins un document"); return; }
    setBusy("pub");
    try {
      await axios.put(`${API}${base}/portal-docs`, { docs, message }, { headers: authH() });
      toast.success(run.portal_published_at ? "Documents du portail mis à jour" : "Documents envoyés au portail du client");
      onChanged?.();
    } catch (e) { err(e); } finally { setBusy(""); }
  };
  const unpublish = async () => {
    if (!window.confirm("Retirer tous les documents de cette période du portail client ?")) return;
    setBusy("un");
    try {
      await axios.delete(`${API}${base}/portal-docs`, { headers: authH() });
      toast.success("Documents retirés du portail"); onChanged?.();
    } catch (e) { err(e); } finally { setBusy(""); }
  };

  const downloads = (run.portal_downloads || []).length;
  return (
    <Section title="Portail client — transmettre les documents" icon={Globe} testId="paie-run-portal-publish"
      right={run.portal_published_at && <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700" data-testid="paie-portal-published-at"><CheckCircle2 className="w-3.5 h-3.5" /> Publié le {String(run.portal_published_at).slice(0, 10)}{downloads ? ` · ${downloads} téléchargement(s)` : ""}</span>}>
      {!settings.portal_tile_enabled && (
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="paie-portal-tile-off-warning">
          La tuile « Paie » n'est pas activée dans le portail de ce client (hub Paie ou Gestion des accès) — il ne verra pas ces documents tant qu'elle ne l'est pas.
        </p>
      )}
      <div className="grid sm:grid-cols-2 gap-1.5">
        {kinds.map((k) => (
          <div key={k.kind} className="flex flex-col">
            <Check label={k.label} checked={!!docs[k.kind]} onChange={(v) => setDocs((d) => ({ ...d, [k.kind]: v }))} testId={`paie-portal-doc-${k.kind}`} />
            <span className="text-[10px] text-slate-400 ml-6">{k.desc}</span>
          </div>
        ))}
      </div>
      <input className={inputCls} placeholder="Message au client (facultatif) — ex. : Déposez le fichier CPA-005 avant jeudi 15 h" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="paie-portal-message" />
      <div className="flex flex-wrap gap-2">
        <Btn tone="orange" icon={Send} busy={busy === "pub"} onClick={publish} testId="paie-portal-publish-btn">{run.portal_published_at ? "Mettre à jour au portail" : "Envoyer au portail client"}</Btn>
        {run.portal_published_at && <Btn tone="slate" icon={Undo2} busy={busy === "un"} onClick={unpublish} testId="paie-portal-unpublish-btn">Retirer du portail</Btn>}
      </div>
      <p className="text-[10px] text-slate-400">Le client ne voit aucun calcul ni sommaire : uniquement les fichiers cochés, générés à la demande depuis cette période.</p>
    </Section>
  );
}
